/**
 * FoodPickerModal — "בחר מזון" / "בחר מזון חלופי" (nutrition menu builder).
 *
 * The single picker behind both "add food" and "add substitute": `mode` only
 * changes the header title, the returned ingredient is identical. Clicking a
 * row hands a fully-formed ingredient to `onSelect` and closes immediately —
 * no confirm step, coaches add many rows in a row.
 *
 * Catalog values are PER 100 GRAMS. The inline "custom food" tab is PER
 * SERVING (`foodType: "customFood"`), matching calcIngredientNutrition, and is
 * never persisted to the catalog — reusable foods are added on /dashboard/foods.
 *
 * Search is a NAME-PREFIX search, debounced and server-paginated: only the
 * pages the coach actually asked for ever reach Redux.
 *
 * Error codes: CK-10140 (search), CK-10141 (load more),
 *              CK-10142 (select a catalog food), CK-10143 (inline custom food).
 */

import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  ArrowDown,
  ArrowUp,
  BowlFood,
  MagnifyingGlass,
  NotePencil,
  X,
} from "@phosphor-icons/react";

import { useSearchFoodsQuery } from "../../../store/services/nutritionFoodsApi";
import { GRAM_UNIT } from "../../../utils/nutritionCalc";
import EmptyDraw from "../../ui/EmptyDraw";
import ModalCloseButton from "../../ui/ModalCloseButton";
import Input from "../../ui/Input";
import { useToast } from "../../../contexts/ToastContext";
import "./FoodPickerModal.css";
import ContentLoader from "../../shared/ContentLoader";
import LoaderLoop from "../../shared/loader/LoaderLoop";

// ============================================================================
// Constants
// ============================================================================

const PAGE_SIZE = 25;
const SEARCH_DEBOUNCE_MS = 350;

const TABS = [
  { id: "catalog", label: "מאגר המזונות", icon: BowlFood },
  { id: "custom", label: "מזון מותאם אישית", icon: NotePencil },
];

/** Sortable result columns, in RTL display order. */
const COLUMNS = [
  { key: "foodName", label: "מאכל", type: "text" },
  { key: "calories", label: "קלוריות", type: "number", unit: 'קק"ל' },
  { key: "carbohydrates", label: "פחמימות", type: "number", unit: "גרם" },
  { key: "protein", label: "חלבון", type: "number", unit: "גרם" },
  { key: "fat", label: "שומן", type: "number", unit: "גרם" },
];

/**
 * Macro range filters as chips. "הכל" is always first and selected by default;
 * every other chip maps to the min/max keys searchFoods already understands.
 */
const RANGE_FILTERS = [
  {
    key: "calories",
    label: "קלוריות",
    minKey: "minCalories",
    maxKey: "maxCalories",
    options: [
      { id: "low", label: "עד 100", max: 100 },
      { id: "mid", label: "100–250", min: 100, max: 250 },
      { id: "high", label: "מעל 250", min: 250 },
    ],
  },
  {
    key: "protein",
    label: "חלבון",
    minKey: "minProtein",
    maxKey: "maxProtein",
    options: [
      { id: "low", label: "עד 5", max: 5 },
      { id: "mid", label: "5–15", min: 5, max: 15 },
      { id: "high", label: "מעל 15", min: 15 },
    ],
  },
  {
    key: "carbohydrates",
    label: "פחמימות",
    minKey: "minCarbohydrates",
    maxKey: "maxCarbohydrates",
    options: [
      { id: "low", label: "עד 10", max: 10 },
      { id: "mid", label: "10–30", min: 10, max: 30 },
      { id: "high", label: "מעל 30", min: 30 },
    ],
  },
  {
    key: "fat",
    label: "שומן",
    minKey: "minFat",
    maxKey: "maxFat",
    options: [
      { id: "low", label: "עד 3", max: 3 },
      { id: "mid", label: "3–15", min: 3, max: 15 },
      { id: "high", label: "מעל 15", min: 15 },
    ],
  },
];

const ALL_RANGES = RANGE_FILTERS.reduce(
  (acc, filter) => ({ ...acc, [filter.key]: "all" }),
  {}
);

const EMPTY_CUSTOM_FORM = {
  foodName: "",
  calories: "",
  protein: "",
  carbohydrates: "",
  fat: "",
};

const CUSTOM_MACRO_FIELDS = [
  { field: "calories", label: 'קלוריות למנה (קק"ל)' },
  { field: "carbohydrates", label: "פחמימות למנה (גרם)" },
  { field: "protein", label: "חלבון למנה (גרם)" },
  { field: "fat", label: "שומן למנה (גרם)" },
];

// ============================================================================
// Helpers (presentation only)
// ============================================================================

/** A fresh ingredient id. crypto.randomUUID everywhere it exists. */
const makeIngredientId = () =>
  typeof crypto !== "undefined" && typeof crypto.randomUUID === "function"
    ? crypto.randomUUID()
    : `ing-${Date.now()}-${Math.random().toString(36).slice(2, 10)}`;

const toNumber = (value) => {
  const num = Number(value);
  return Number.isFinite(num) ? num : 0;
};

/** Round for display without a trailing ".0". */
const fmt = (value) => {
  const num = Number(value);
  if (!Number.isFinite(num)) return "—";
  return String(Math.round(num * 10) / 10);
};

const validateCustomForm = (form) => {
  const errors = {};

  if (!form.foodName || !form.foodName.trim()) {
    errors.foodName = "יש להזין שם למזון";
  }

  CUSTOM_MACRO_FIELDS.forEach(({ field, label }) => {
    const raw = String(form[field] ?? "").trim();
    if (raw === "") {
      errors[field] = `יש להזין ${label}`;
      return;
    }
    const num = Number(raw);
    if (!Number.isFinite(num) || num < 0) {
      errors[field] = "יש להזין מספר חיובי";
    }
  });

  return errors;
};

// ============================================================================
// Component
// ============================================================================

/**
 * @param {"food"|"substitute"} mode header title only
 * @param {string} clinicId
 * @param {function} onClose
 * @param {function} onSelect receives a fully-formed ingredient; the modal closes
 */
const FoodPickerModal = ({ mode = "food", clinicId, onClose, onSelect }) => {
  const { showError } = useToast();

  const [activeTab, setActiveTab] = useState("catalog");

  // ─── Search (name-prefix → debounced) ───
  const [searchInput, setSearchInput] = useState("");
  const [searchQuery, setSearchQuery] = useState("");
  useEffect(() => {
    const timer = setTimeout(
      () => setSearchQuery(searchInput.trim()),
      SEARCH_DEBOUNCE_MS
    );
    return () => clearTimeout(timer);
  }, [searchInput]);

  // ─── Macro range chips ───
  const [ranges, setRanges] = useState(ALL_RANGES);

  const filters = useMemo(() => {
    const next = {};
    RANGE_FILTERS.forEach((filter) => {
      const selectedId = ranges[filter.key];
      if (!selectedId || selectedId === "all") return;
      const option = filter.options.find((opt) => opt.id === selectedId);
      if (!option) return;
      if (option.min !== undefined) next[filter.minKey] = option.min;
      if (option.max !== undefined) next[filter.maxKey] = option.max;
    });
    return next;
  }, [ranges]);

  const hasActiveRanges = useMemo(
    () => Object.values(ranges).some((value) => value !== "all"),
    [ranges]
  );

  // ─── Pagination (accumulate pages; reset on any query/filter change) ───
  const [cursor, setCursor] = useState(null);
  const [foods, setFoods] = useState([]);
  const queryKey = useMemo(
    () => JSON.stringify({ searchQuery, filters }),
    [searchQuery, filters]
  );
  const lastQueryKeyRef = useRef(queryKey);

  useEffect(() => {
    if (lastQueryKeyRef.current === queryKey) return;
    lastQueryKeyRef.current = queryKey;
    setCursor(null);
    setFoods([]);
  }, [queryKey]);

  const {
    data,
    isLoading,
    isFetching,
    error: searchError,
    refetch,
  } = useSearchFoodsQuery(
    { clinicId, searchQuery, filters, cursor, pageSize: PAGE_SIZE },
    { skip: !clinicId }
  );

  useEffect(() => {
    if (!data?.foods) return;
    setFoods((prev) => {
      if (cursor === null) return data.foods;
      const seen = new Set(prev.map((food) => food.id));
      return [...prev, ...data.foods.filter((food) => !seen.has(food.id))];
    });
  }, [data, cursor]);

  useEffect(() => {
    if (!searchError) return;
    console.error("[CK-10140] Error searching foods in the picker:", {
      errorCode: "CK-10140",
      errorMessage: searchError?.message || "unknown",
      clinicId,
      hasQuery: !!searchQuery,
      timestamp: new Date().toISOString(),
    });
  }, [searchError, clinicId, searchQuery]);

  const handleLoadMore = useCallback(() => {
    if (!data?.hasMore || !data?.nextCursor || isFetching) return;
    try {
      setCursor(data.nextCursor);
    } catch (error) {
      console.error("[CK-10141] Error loading the next foods page:", {
        errorCode: "CK-10141",
        errorMessage: error.message,
        clinicId,
        timestamp: new Date().toISOString(),
      });
      showError("טעינת המזונות הנוספים נכשלה, נסו שוב");
    }
  }, [data, isFetching, clinicId, showError]);

  // ─── Sorting (applies to the pages already loaded) ───
  const [sort, setSort] = useState({ key: "foodName", dir: "asc" });

  const toggleSort = useCallback((key) => {
    setSort((prev) =>
      prev.key === key
        ? { key, dir: prev.dir === "asc" ? "desc" : "asc" }
        : { key, dir: key === "foodName" ? "asc" : "desc" }
    );
  }, []);

  const sortedFoods = useMemo(() => {
    const column = COLUMNS.find((col) => col.key === sort.key);
    if (!column) return foods;
    const factor = sort.dir === "asc" ? 1 : -1;
    return [...foods].sort((a, b) => {
      if (column.type === "text") {
        return (
          String(a[column.key] || "").localeCompare(
            String(b[column.key] || ""),
            "he"
          ) * factor
        );
      }
      return (toNumber(a[column.key]) - toNumber(b[column.key])) * factor;
    });
  }, [foods, sort]);

  // ─── Selection ───
  const handleSelectFood = useCallback(
    (food) => {
      try {
        onSelect({
          ingredientId: makeIngredientId(),
          foodName: food.foodName,
          calories: toNumber(food.calories),
          protein: toNumber(food.protein),
          carbohydrates: toNumber(food.carbohydrates),
          fat: toNumber(food.fat),
          sizeOptions: food.sizeOptions || {},
          amount: 0,
          selectedAmount: GRAM_UNIT,
          connectedIngredients: [],
        });
        onClose();
      } catch (error) {
        console.error("[CK-10142] Error adding a food to the menu:", {
          errorCode: "CK-10142",
          errorMessage: error.message,
          clinicId,
          foodId: food?.id,
          timestamp: new Date().toISOString(),
        });
        showError("הוספת המזון נכשלה, נסו שוב");
      }
    },
    [onSelect, onClose, clinicId, showError]
  );

  // ─── Inline custom food (this menu only, never persisted) ───
  const [customForm, setCustomForm] = useState(EMPTY_CUSTOM_FORM);
  const [customErrors, setCustomErrors] = useState({});

  const setCustomField = useCallback((field, value) => {
    setCustomForm((prev) => ({ ...prev, [field]: value }));
  }, []);

  const handleAddCustomFood = useCallback(() => {
    const errors = validateCustomForm(customForm);
    setCustomErrors(errors);
    if (Object.keys(errors).length > 0) return;

    try {
      onSelect({
        ingredientId: makeIngredientId(),
        foodName: customForm.foodName.trim(),
        calories: toNumber(customForm.calories),
        protein: toNumber(customForm.protein),
        carbohydrates: toNumber(customForm.carbohydrates),
        fat: toNumber(customForm.fat),
        sizeOptions: {},
        foodType: "customFood",
        amount: 0,
        selectedAmount: GRAM_UNIT,
        connectedIngredients: [],
      });
      onClose();
    } catch (error) {
      console.error("[CK-10143] Error adding an inline custom food:", {
        errorCode: "CK-10143",
        errorMessage: error.message,
        clinicId,
        timestamp: new Date().toISOString(),
      });
      showError("הוספת המזון המותאם אישית נכשלה, נסו שוב");
    }
  }, [customForm, onSelect, onClose, clinicId, showError]);

  // ─── Escape closes ───
  useEffect(() => {
    const handleKeyDown = (event) => {
      if (event.key === "Escape") onClose();
    };
    document.addEventListener("keydown", handleKeyDown);
    return () => document.removeEventListener("keydown", handleKeyDown);
  }, [onClose]);

  const showInitialLoader = isLoading && foods.length === 0;
  const isEmpty = !showInitialLoader && !searchError && foods.length === 0;
  const hasFiltersApplied = !!searchQuery || hasActiveRanges;
  const title = mode === "substitute" ? "בחר מזון חלופי" : "בחר מזון";

  return (
    <div className="nfpk-overlay" onClick={onClose} role="presentation">
      <div
        className="nfpk-modal"
        onClick={(event) => event.stopPropagation()}
        role="dialog"
        aria-modal="true"
        aria-label={title}
      >
        {/* ===== Header ===== */}
        <div className="nfpk-header">
          <div className="nfpk-header-title">
            <span className="nfpk-title-icon">
              <BowlFood size={20} weight="fill" />
            </span>
            <div className="nfpk-title-text">
              <span className="nfpk-eyebrow">בניית תפריט</span>
              <h2>{title}</h2>
            </div>
          </div>
          <ModalCloseButton onClick={onClose} />
        </div>

        {/* ===== Tabs ===== */}
        <div className="nfpk-tabs" role="tablist" aria-label="מקור המזון">
          {TABS.map((tab) => {
            const Icon = tab.icon;
            const isActive = activeTab === tab.id;
            return (
              <button
                key={tab.id}
                type="button"
                role="tab"
                aria-selected={isActive}
                className={`nfpk-tab ${isActive ? "active" : ""}`}
                onClick={() => setActiveTab(tab.id)}
              >
                <Icon size={15} weight={isActive ? "fill" : "regular"} />
                <span>{tab.label}</span>
              </button>
            );
          })}
        </div>

        {/* ===== Body ===== */}
        <div className="nfpk-body">
          {activeTab === "catalog" ? (
            <>
              <div className="nfpk-search-box">
                <MagnifyingGlass size={16} weight="bold" />
                <input
                  type="text"
                  value={searchInput}
                  onChange={(event) => setSearchInput(event.target.value)}
                  placeholder="חיפוש מזון לפי תחילת השם..."
                  aria-label="חיפוש מזון"
                />
                {searchInput && (
                  <button
                    type="button"
                    className="nfpk-search-clear"
                    onClick={() => setSearchInput("")}
                    aria-label="ניקוי חיפוש"
                  >
                    <X size={14} weight="bold" />
                  </button>
                )}
              </div>

              {/* Macro range chips — "הכל" first in every group */}
              <div className="nfpk-filters">
                {RANGE_FILTERS.map((filter) => (
                  <div className="nfpk-filter-group" key={filter.key}>
                    <span className="nfpk-filter-label">
                      {filter.label}{" "}
                      <span className="nfpk-filter-unit">(ל-100 גרם)</span>
                    </span>
                    <div className="nfpk-chips">
                      <button
                        type="button"
                        className={`nfpk-chip ${
                          ranges[filter.key] === "all" ? "active" : ""
                        }`}
                        aria-pressed={ranges[filter.key] === "all"}
                        onClick={() =>
                          setRanges((prev) => ({ ...prev, [filter.key]: "all" }))
                        }
                      >
                        הכל
                      </button>
                      {filter.options.map((option) => {
                        const isActive = ranges[filter.key] === option.id;
                        return (
                          <button
                            key={option.id}
                            type="button"
                            className={`nfpk-chip ${isActive ? "active" : ""}`}
                            aria-pressed={isActive}
                            onClick={() =>
                              setRanges((prev) => ({
                                ...prev,
                                [filter.key]: isActive ? "all" : option.id,
                              }))
                            }
                          >
                            {option.label}
                          </button>
                        );
                      })}
                    </div>
                  </div>
                ))}
              </div>

              {/* Results */}
              {showInitialLoader ? (
                <ContentLoader message="טוען מזונות..." />
              ) : searchError ? (
                <div className="nfpk-empty">
                  <EmptyDraw variant="search" size="sm" />
                  <p>טעינת מאגר המזונות נכשלה</p>
                  <button
                    type="button"
                    className="nfpk-secondary-btn"
                    onClick={() => refetch()}
                  >
                    נסו שוב
                  </button>
                </div>
              ) : isEmpty ? (
                <div className="nfpk-empty">
                  <EmptyDraw
                    variant={hasFiltersApplied ? "search" : "template"}
                    size="sm"
                  />
                  <p>
                    {hasFiltersApplied
                      ? "לא נמצאו מזונות התואמים לחיפוש"
                      : "מאגר המזונות ריק כרגע"}
                  </p>
                  <p className="nfpk-empty-hint">
                    אפשר להוסיף מזון חד-פעמי בלשונית "מזון מותאם אישית"
                  </p>
                </div>
              ) : (
                <>
                  <div className="nfpk-table-wrap">
                    <table className="nfpk-table">
                      <caption className="nfpk-table-caption">
                        כל הערכים ל-100 גרם. לחיצה על שורה מוסיפה את המזון
                        לתפריט
                      </caption>
                      <thead>
                        <tr>
                          {COLUMNS.map((column) => {
                            const isSorted = sort.key === column.key;
                            const SortIcon =
                              sort.dir === "asc" ? ArrowUp : ArrowDown;
                            return (
                              <th
                                key={column.key}
                                scope="col"
                                aria-sort={
                                  isSorted
                                    ? sort.dir === "asc"
                                      ? "ascending"
                                      : "descending"
                                    : "none"
                                }
                              >
                                <button
                                  type="button"
                                  className={`nfpk-sort-btn ${
                                    isSorted ? "active" : ""
                                  }`}
                                  onClick={() => toggleSort(column.key)}
                                >
                                  <span>{column.label}</span>
                                  {isSorted && (
                                    <SortIcon size={11} weight="fill" />
                                  )}
                                </button>
                              </th>
                            );
                          })}
                        </tr>
                      </thead>
                      <tbody>
                        {sortedFoods.map((food) => (
                          <tr
                            key={food.id}
                            className="nfpk-row"
                            role="button"
                            tabIndex={0}
                            onClick={() => handleSelectFood(food)}
                            onKeyDown={(event) => {
                              if (event.key === "Enter" || event.key === " ") {
                                event.preventDefault();
                                handleSelectFood(food);
                              }
                            }}
                          >
                            <td data-label="מאכל" className="nfpk-cell-name">
                              <span className="nfpk-food-name">
                                {food.foodName}
                              </span>
                              {food.source === "clinic" && (
                                <span className="nfpk-badge nfpk-badge-clinic">
                                  מזון המרפאה
                                </span>
                              )}
                            </td>
                            <td data-label='קלוריות (קק"ל ל-100 גרם)'>
                              <span className="nfpk-value">
                                {fmt(food.calories)}
                              </span>
                            </td>
                            <td data-label="פחמימות (גרם ל-100 גרם)">
                              <span className="nfpk-value">
                                {fmt(food.carbohydrates)}
                              </span>
                            </td>
                            <td data-label="חלבון (גרם ל-100 גרם)">
                              <span className="nfpk-value">
                                {fmt(food.protein)}
                              </span>
                            </td>
                            <td data-label="שומן (גרם ל-100 גרם)">
                              <span className="nfpk-value">{fmt(food.fat)}</span>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>

                  <div className="nfpk-results-footer">
                    <span className="nfpk-count">
                      {sortedFoods.length} מזונות מוצגים
                    </span>
                    {data?.hasMore && (
                      <button
                        type="button"
                        className="nfpk-loadmore"
                        onClick={handleLoadMore}
                        disabled={isFetching}
                      >
                        {isFetching ? (
                          <>
                            <LoaderLoop size="xs" />
                            <span>טוען...</span>
                          </>
                        ) : (
                          <span>טען עוד</span>
                        )}
                      </button>
                    )}
                  </div>
                </>
              )}
            </>
          ) : (
            /* ===== Inline custom food ===== */
            <>
              <p className="nfpk-note">
                מזון מותאם אישית נשמר בתפריט הזה בלבד ואינו נוסף למאגר המזונות.
                הערכים כאן הם <strong>למנה אחת</strong> ולא ל-100 גרם. כדי להוסיף
                מזון קבוע לשימוש חוזר, היכנסו לעמוד "מאגר המזונות".
              </p>

              <Input
                label="שם המזון"
                name="customFoodName"
                value={customForm.foodName}
                onChange={(event) =>
                  setCustomField("foodName", event.target.value)
                }
                placeholder="לדוגמה: סלט הבית של דנה"
                error={customErrors.foodName}
                required
              />

              <div className="nfpk-field-grid">
                {CUSTOM_MACRO_FIELDS.map(({ field, label }) => (
                  <Input
                    key={field}
                    type="number"
                    lang="en"
                    min="0"
                    step="0.1"
                    label={label}
                    name={`custom-${field}`}
                    value={customForm[field]}
                    onChange={(event) =>
                      setCustomField(field, event.target.value)
                    }
                    error={customErrors[field]}
                    required
                  />
                ))}
              </div>

              <div className="nfpk-custom-actions">
                <button
                  type="button"
                  className="nfpk-primary-btn"
                  onClick={handleAddCustomFood}
                >
                  הוספה לתפריט
                </button>
                <button
                  type="button"
                  className="nfpk-secondary-btn"
                  onClick={onClose}
                >
                  ביטול
                </button>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default FoodPickerModal;
